import type {
  ColumnOrderState,
  ColumnVisibilityState,
} from '@tanstack/react-table';

/** `UserSettingType.TableColumnVisibility` in `Ditio.Model/Settings/UserSettings.cs`. */
export const TABLE_COLUMN_VISIBILITY_SETTING_TYPE = 50;

export type ConfigurableTableName = 'alerts' | 'resources' | 'users';

/**
 * The blob stored under `TABLE_COLUMN_VISIBILITY_SETTING_TYPE`.
 *
 * A table's hidden columns stay where they have always been — at the top level
 * under the table's own name — and the order lives in a sibling `columnOrder`
 * map rather than nested beside them. That layout is what makes the two halves
 * independent for a client that only knows about one of them: a bundle
 * predating column ordering reads `settings[tableName]` and finds exactly the
 * visibility map it expects, and its writes (`{ ...settings, [tableName]: … }`)
 * carry `columnOrder` through untouched. Nesting the halves under the table key
 * would instead make an old bundle read the wrapper object as a visibility map
 * — unhiding every column — and then drop the order on its next write.
 *
 * Both halves are sparse: only hidden columns are stored, so a column added
 * later is visible by default, and a table whose order matches the definition
 * order stores no order at all.
 */
export type TableColumnSettings = Partial<
  Record<ConfigurableTableName, ColumnVisibilityState>
> & {
  columnOrder?: Partial<Record<ConfigurableTableName, ColumnOrderState>>;
};

export interface UserSettingsViewModel<TSettings> {
  companyId: string;
  id: string;
  settings: TSettings | null;
  type: number;
  userId: string;
}
